// Seed state — spec §25. Everything here is editable by the founder.

import { EPICS, EPIC_CLASS, EPIC_MARKET, EPIC_STRATEGY_STATUS, REGULATED_ACTIVITIES, SAUDI_PIPELINE_FIELDS, MODULES } from './constants';
import { GATE_OUTCOMES } from './gateOutcomes';
import { seedWorkPackages, readyMovesFromPackages } from './workPackages';
import { newAction, cleanupExecutionQueue } from './actions';
import { CANONICAL_SPRINT_START, sprintInitialization } from './sprint';

export const SCHEMA_VERSION = 7;

// Bootstrap Day 1. Runtime calendar uses Sprint Initialization (T0).
export const DEFAULT_SPRINT_START = CANONICAL_SPRINT_START;

// Revision 01 operating hypotheses for the Dubai first-deal funnel.
// Editable in Settings. Adjust from evidence after Day 15.
export const DEFAULT_FUNNEL_TARGETS = {
  universe: 85,
  qualifiedTargets: 35,
  tierA: 12,
  conversations: 12,
  qualifiedOpportunities: 5,
  assets: 3,
  candidates: 2,
  primary: 1,
  commercialCommitment: 1,
};

export const EMPTY_FUNNEL_TARGETS = {
  universe: 0,
  qualifiedTargets: 0,
  tierA: 0,
  conversations: 0,
  qualifiedOpportunities: 0,
  assets: 0,
  candidates: 0,
  primary: 0,
  commercialCommitment: 0,
};

export const DEFAULT_AGING_THRESHOLDS = {
  tierAContacted: 3,
  discoveryNextAction: 1,
  qualifiedOpportunity: 5,
  d001Candidate: 3,
};

function emptySaudiPipeline() {
  const o = {};
  for (const f of SAUDI_PIPELINE_FIELDS) o[f.key] = 0;
  return o;
}

/** Neutral skeleton — mirrors API seed. Used by migrate(); never injects Blocks DNA. */
export function neutralSkeletonState(today = DEFAULT_SPRINT_START) {
  return {
    meta: {
      schemaVersion: SCHEMA_VERSION,
      createdAt: today,
      updatedAt: today,
      product: 'Bridge45',
      company: '',
    },
    settings: {
      founderName: '',
      currency: 'AED',
      theme: 'system',
      showTermHelp: true,
      useRealClock: true,
      pinnedDate: today,
      strategyApprovedAt: '',
      sprintStart: today,
      sprint: sprintInitialization({ start: today, initializedAt: today }),
      alertThresholds: {
        idleProposalDays: 5,
        idleTierADays: 5,
        runwayWatchMonths: 6,
        runwayCriticalMonths: 3,
        materialSpend: 5000,
      },
      funnelTargets: { ...EMPTY_FUNNEL_TARGETS },
      agingThresholds: { ...DEFAULT_AGING_THRESHOLDS },
      jira: { baseUrl: '' },
      planSetup: { status: 'needs_setup' },
    },
    accounts: [],
    opportunities: [],
    assetCandidates: [],
    crmActivities: [],
    runway: {
      startingCash: '',
      cashToday: '',
      monthlyBurn: '',
      baselineMonthlyBurn: '',
      minViableBurn: '',
      expectedProofSpend: '',
    },
    expenses: [],
    approvals: [],
    d001: {
      exists: false,
      primaryOpportunityId: '',
      primaryHistory: [],
      stage: 'Target',
      stageHistory: [],
      sponsor: {
        company: '',
        decisionMaker: '',
        relationshipStrength: '',
        problem: '',
        whyNow: '',
        currentAlternative: '',
        commercialObjective: '',
        decisionProcess: '',
        authorityConfirmed: false,
        agreedNextStep: false,
      },
      asset: {
        name: '',
        location: '',
        type: '',
        indicativeValue: '',
        income: '',
        ownership: '',
        valuation: '',
        documentsAvailable: '',
        encumbrances: '',
        thesis: '',
        repeatPotential: '',
        namedAssetExists: false,
        titleInfoAvailable: false,
        economicsUnderstood: false,
        passesScreen: false,
      },
      regulated: Object.fromEntries(
        REGULATED_ACTIVITIES.map((a) => [a.key, { responsible: 'Unknown', note: '' }]),
      ),
      partners: [],
      capital: {
        targetSegment: '',
        expectedTicket: '',
        acceptanceCriteria: '',
        distributionLimitations: '',
        dealInterest: '',
        conversations: [],
      },
      constraint: { blocker: '', owner: 'Founder', due: '', evidenceNeeded: '' },
      constraintHistory: [],
    },
    saudi: {
      signals: { commercial: 'none', useCase: 'none', regulatedPartner: 'none', government: 'none' },
      signalNotes: { commercial: '', useCase: '', regulatedPartner: '', government: '' },
      offers: {
        A: { name: '', pipeline: emptySaudiPipeline(), notes: '' },
        B: { name: '', pipeline: emptySaudiPipeline(), notes: '' },
      },
      offerC: {
        name: '',
        regaContact: '',
        proptechHub: '',
        sandboxStatus: '',
        nextIntake: '',
        workingSession: '',
        pilotCriteria: '',
        technologyEvaluation: '',
        registryIntegration: '',
      },
      opportunities: [],
    },
    infra: { opportunities: [], features: [] },
    evidence: [],
    top3: [],
    closeouts: [],
    sprintReviews: [],
    timeLog: [],
    workPackages: [],
    recommendationDecisions: [],
    plan: { epics: [], definitionId: '', definitionVersion: 0 },
    planDefinition: {
      schemaVersion: 2,
      status: 'needs_setup',
      definitionId: '',
      version: 0,
      name: '',
      lockedStrategy: {
        dubai: '',
        saudi: '',
        qatarBahrain: '',
        infrastructure: '',
        horizonDays: 45,
      },
      timeTargets: [],
      markets: [],
      workstreams: [],
      stages: [],
      epics: [],
      gateOutcomes: [],
      workPackageTemplates: [],
      provenance: { source: 'manual', note: 'Awaiting organization plan setup' },
    },
    reviews: [],
    decisions: [],
    exceptions: [],
    bridgeBriefings: [],
    bridgeRecommendations: [],
    bridgeAsks: [],
    planVersions: [],
    planJobs: [],
    planAutopilot: { enabled: false },
    productRequests: [],
    gateReviews: {},
    ui: { dismissedAlerts: [] },
  };
}

/** Blocks Estate fixture for unit tests / offline demos — not used by API migrate. */
export function seedState(today = DEFAULT_SPRINT_START) {
  const workPackages = seedWorkPackages();
  const top3 = cleanupExecutionQueue(
    readyMovesFromPackages(
      workPackages.filter((p) => p.setup),
      [],
      today,
    ).map((m) => newAction({ ...m, id: `wp-move-${m.workPackageId}` }, today)),
    today,
  );
  return {
    meta: {
      schemaVersion: SCHEMA_VERSION,
      createdAt: today,
      updatedAt: today,
      product: 'Bridge45',
      company: 'Blocks Estate',
    },

    settings: {
      founderName: '',
      currency: 'AED',
      // 'system' follows the operating system; 'light' and 'dark' override it.
      theme: 'system',
      // Show contextual (i) definitions until the founder prefers a cleaner interface.
      showTermHelp: true,
      // If true, the app uses the real clock. If false it uses `pinnedDate`,
      // which is useful for reviewing a past day or rehearsing a gate.
      useRealClock: true,
      pinnedDate: today,
      strategyApprovedAt: '2026-08-13',
      // Day 1 of the sprint. Gates (15 / 30 / 45) are counted from Sprint Initialization.
      sprintStart: today,
      sprint: sprintInitialization({ start: today, initializedAt: today }),
      alertThresholds: {
        idleProposalDays: 5,
        idleTierADays: 5,
        runwayWatchMonths: 6,
        runwayCriticalMonths: 3,
        materialSpend: 5000,
      },
      funnelTargets: { ...DEFAULT_FUNNEL_TARGETS },
      agingThresholds: { ...DEFAULT_AGING_THRESHOLDS },
      jira: { baseUrl: '' },
      planSetup: { status: 'complete' },
    },

    // ---- Revision 01: the commercial funnel that produces D001 (Dubai) ----
    // Bridge45 stores only enough pipeline to answer founder-level questions.
    // Nexus remains the system of record; Sync from Nexus hydrates founder-thin shells.
    accounts: [],
    opportunities: [],
    assetCandidates: [],
    crmActivities: [],

    runway: {
      startingCash: '',
      cashToday: '',
      monthlyBurn: '',
      baselineMonthlyBurn: '',
      minViableBurn: '',
      expectedProofSpend: '',
    },

    expenses: [],

    // Regulator-sensitive communications held for approval (spec §6G, E11).
    approvals: [],

    d001: {
      exists: false,
      // Revision 01: D001 is now the deep transaction record for whichever
      // opportunity currently holds the Primary designation.
      primaryOpportunityId: '',
      primaryHistory: [],
      stage: 'Target',
      stageHistory: [],
      sponsor: {
        company: '',
        decisionMaker: '',
        relationshipStrength: '',
        problem: '',
        whyNow: '',
        currentAlternative: '',
        commercialObjective: '',
        decisionProcess: '',
        authorityConfirmed: false,
        agreedNextStep: false,
      },
      asset: {
        name: '',
        location: '',
        type: '',
        indicativeValue: '',
        income: '',
        ownership: '',
        valuation: '',
        documentsAvailable: '',
        encumbrances: '',
        thesis: '',
        repeatPotential: '',
        namedAssetExists: false,
        titleInfoAvailable: false,
        economicsUnderstood: false,
        passesScreen: false,
      },
      regulated: Object.fromEntries(
        REGULATED_ACTIVITIES.map((a) => [a.key, { responsible: 'Unknown', note: '' }])
      ),
      partners: [],
      capital: {
        targetSegment: '',
        expectedTicket: '',
        acceptanceCriteria: '',
        distributionLimitations: '',
        dealInterest: '',
        conversations: [],
      },
      constraint: {
        blocker: '',
        owner: 'Founder',
        due: '',
        evidenceNeeded: 'E2 — a named sponsor confirms a relevant problem or use case (named assets/documents are E3).',
      },
      constraintHistory: [],
    },

    saudi: {
      signals: { commercial: 'none', useCase: 'none', regulatedPartner: 'none', government: 'none' },
      signalNotes: { commercial: '', useCase: '', regulatedPartner: '', government: '' },
      offers: {
        A: { name: 'Contribution OS', pipeline: emptySaudiPipeline(), notes: '' },
        B: { name: 'Blocks Infrastructure', pipeline: emptySaudiPipeline(), notes: '' },
      },
      offerC: {
        name: 'REGA / Tokenization Path',
        regaContact: '',
        proptechHub: '',
        sandboxStatus: '',
        nextIntake: '',
        workingSession: '',
        pilotCriteria: '',
        technologyEvaluation: '',
        registryIntegration: '',
      },
      opportunities: [],
    },

    infra: {
      opportunities: [],
      features: [
        ...MODULES.Core.map((m) => ({ id: `mod-core-${slug(m)}`, name: m, group: 'Core', classification: 'EXISTS' })),
        ...MODULES.Optional.map((m) => ({ id: `mod-opt-${slug(m)}`, name: m, group: 'Optional', classification: 'CONFIGURE' })),
      ],
    },

    evidence: [],
    top3,
    closeouts: [],
    sprintReviews: [],
    timeLog: [],
    workPackages,
    recommendationDecisions: [],

    plan: {
      epics: EPICS.map((e) => ({
        ...e,
        status: 'Not started',
        owner: 'Founder',
        start: '',
        end: '',
        evidenceRequired: DEFAULT_EPIC_EVIDENCE[e.id] || '',
        acceptance: '',
        jiraSpaceKey: '',
        jiraId: '',
        stories: [],
      })),
    },
    planDefinition: {
      schemaVersion: 2,
      status: 'active',
      definitionId: 'pd-blocks-fixture',
      version: 1,
      name: 'Blocks Estate 45-Day Strategy',
      lockedStrategy: {
        dubai: 'Active',
        saudi: 'Validation',
        qatarBahrain: 'Parked',
        infrastructure: 'Cash Engine',
        horizonDays: 45,
      },
      timeTargets: [
        { key: 'dubai', label: 'Dubai', target: 55 },
        { key: 'saudi', label: 'Saudi', target: 20 },
        { key: 'infrastructure', label: 'Infrastructure', target: 10 },
        { key: 'fundraising', label: 'Fundraising / co-founder', target: 10 },
        { key: 'other', label: 'Other / Qatar / Bahrain / admin', target: 5 },
      ],
      markets: [
        { key: 'dubai', label: 'Dubai', posture: 'Active' },
        { key: 'saudi', label: 'Saudi', posture: 'Validation' },
        { key: 'infrastructure', label: 'Infrastructure', posture: 'Cash Engine' },
      ],
      workstreams: [
        { key: 'dubai', label: 'Dubai', pipelineTab: true },
        { key: 'saudi', label: 'Saudi', pipelineTab: true },
        { key: 'infrastructure', label: 'Infrastructure', pipelineTab: true },
        { key: 'fundraising', label: 'Fundraising' },
        { key: 'runway', label: 'Runway' },
        { key: 'other', label: 'Other' },
        { key: 'd001', label: 'D001' },
      ],
      pipelines: [
        {
          key: 'dubai',
          name: 'Dubai Pipeline',
          kind: 'asset_origination',
          workstreamKey: 'dubai',
          marketKey: 'dubai',
          defaultMarket: 'Dubai',
          defaultOpportunityType: 'Dubai Founding Asset',
          pipelineTab: true,
          panel: 'primary_deal',
          deepWorkspace: true,
          stages: [
            { id: 'observed', label: 'Observed', order: 0 },
            { id: 'qualified', label: 'Qualified', order: 1, qualifies: true, metricKey: 'qualified' },
            { id: 'asset-identified', label: 'Asset Identified', order: 2, qualifies: true, metricKey: 'assetsIdentified' },
            { id: 'asset-screen', label: 'Asset Screen', order: 3, qualifies: true },
            { id: 'd001-candidate', label: 'D001 Candidate', order: 4, qualifies: true, metricKey: 'candidates' },
            { id: 'primary-d001', label: 'Primary D001', order: 5, qualifies: true, metricKey: 'primary' },
            { id: 'readiness-proposed', label: 'Readiness Proposed', order: 6, qualifies: true },
            { id: 'readiness-active', label: 'Readiness Active', order: 7, qualifies: true },
            { id: 'execution-structuring', label: 'Execution Structuring', order: 8, qualifies: true },
            { id: 'mandate', label: 'Mandate / Implementation', order: 9, qualifies: true },
            { id: 'execution-ready', label: 'Execution Ready', order: 10, qualifies: true },
            { id: 'live', label: 'Live', order: 11, qualifies: true },
            { id: 'parked', label: 'Won / Lost / Parked', order: 12, terminal: true },
          ],
        },
        {
          key: 'saudi',
          name: 'Saudi Pipeline',
          kind: 'regulatory',
          workstreamKey: 'saudi',
          marketKey: 'saudi',
          defaultMarket: 'Saudi',
          pipelineTab: true,
          panel: 'saudi',
          deepWorkspace: false,
        },
        {
          key: 'infrastructure',
          name: 'Infrastructure Pipeline',
          kind: 'commercial',
          workstreamKey: 'infrastructure',
          marketKey: 'infrastructure',
          defaultMarket: 'Infrastructure',
          pipelineTab: true,
          panel: 'infrastructure',
          deepWorkspace: false,
        },
      ],
      stages: [],
      commercialScope: { mode: 'primary_market', primaryMarketKey: 'Dubai', includeTierAAlways: false },
      marketAliases: { Dubai: ['UAE', 'Dubai', 'Abu Dhabi'] },
      primaryDeal: {
        enabled: true,
        label: 'Primary D001',
        candidateKey: 'd001Candidate',
        primaryKey: 'primaryD001',
        emptyCopy:
          'No Primary D001 yet. Build enough opportunity density to choose the strongest first transaction.',
      },
      funnelMetrics: [
        { key: 'universe', label: 'Accounts researched', source: 'count', match: 'universe' },
        { key: 'qualifiedTargets', label: 'Screened accounts', source: 'count', match: 'qualifiedTargets' },
        { key: 'contacted', label: 'Contacted', source: 'account_status', match: 'Contacted' },
        { key: 'engaged', label: 'Engaged', source: 'account_status', match: 'Engaged' },
        { key: 'discovery', label: 'Commercial conversations', source: 'account_status', match: 'Discovery' },
        { key: 'qualified', label: 'Real opportunities', source: 'opp_stage', match: 'Qualified' },
        { key: 'assetsIdentified', label: 'Assets identified', source: 'count', match: 'assetsIdentified' },
        { key: 'assetsInScreen', label: 'Assets in screen', source: 'count', match: 'assetsInScreen' },
        { key: 'candidates', label: 'D001 candidates', source: 'flag', match: 'd001Candidate' },
        { key: 'primary', label: 'Primary D001', source: 'flag', match: 'primaryD001' },
      ],
      agingMetrics: [
        { key: 'tierAContacted', label: 'Tier A contacted idle (business days)' },
        { key: 'discoveryNextAction', label: 'Discovery next-action overdue (business days)' },
        { key: 'qualifiedOpportunity', label: 'Qualified opportunity idle (business days)' },
        { key: 'd001Candidate', label: 'D001 candidate idle (business days)' },
      ],
      funnelTargetsPolicy: 'merge',
      funnelMode: 'asset_d001',
      funnelMetricLabels: {
        universe: 'Accounts researched',
        qualifiedTargets: 'Screened accounts',
        tierA: 'Priority accounts',
        conversations: 'Commercial conversations',
        qualifiedOpportunities: 'Real opportunities',
        assets: 'Assets identified',
        candidates: 'D001 candidates',
        primary: 'Primary D001',
      },
      segmentOptions: [
        'Developer',
        'Asset owner',
        'Portfolio owner',
        'Family office',
        'Fund / investment firm',
        'Sponsor / operator',
        'Regulated institution',
        'Other',
      ],
      uiCopy: {
        funnelTargetsTitle: 'Dubai funnel targets',
        whyUsLabel: 'Why Blocks',
        exportAccountsLabel: 'Dubai accounts',
        deepWorkspaceNavLabel: 'Primary D001',
      },
      coachHints: {
        concentrateDefault: 'Concentrate on Primary D001',
        emptyFunnel: 'No credible asset or opportunity is approaching D001.',
      },
      epics: EPICS.map((e) => ({
        id: e.id,
        title: e.title,
        evidenceRequired: DEFAULT_EPIC_EVIDENCE[e.id] || '',
        epicClass: EPIC_CLASS[e.id] || 'delivery',
        strategyStatus: EPIC_STRATEGY_STATUS[e.id] || 'Active',
        market: EPIC_MARKET[e.id],
        workstream: EPIC_MARKET[e.id] || 'Other',
      })),
      gateOutcomes: GATE_OUTCOMES.map((g) => ({ ...g, epicIds: [...(g.epicIds || [])] })),
      workPackageTemplates: [],
      provenance: { source: 'template', templateId: 'blocks-estate', templateVersion: 3 },
      approvedAt: '2026-08-13T00:00:00.000Z',
    },

    reviews: [],
    decisions: [],
    exceptions: [],
    bridgeBriefings: [],
    bridgeRecommendations: [],
    bridgeAsks: [],
    planVersions: [],
    planJobs: [],
    planAutopilot: { enabled: false },
    productRequests: [],
    gateReviews: {},

    ui: { dismissedAlerts: [] },
  };
}

const DEFAULT_EPIC_EVIDENCE = {
  E01: 'Every strategy change passes through a logged exception with a named cost and a named stop.',
  E02: 'Monthly burn reduced and every material expense carries an evidence objective.',
  E03: 'Founder operating state is maintained daily without a second system.',
  E04: 'A named sponsor with authority and a named asset with title information (E3).',
  E05: 'A licensed partner accepts the D001 transaction architecture (E4).',
  E06: 'Two credible capital conversations and one D001-specific acceptance signal (E4).',
  E07: 'One priced proposal a buyer asked for, and a path to a paid POC (E4/E5).',
  E08: 'At least two of the four Saudi signals supported by named counterparties.',
  E09: 'No work unless an external trigger fires. Evidence = the trigger itself.',
  E10: 'AI output converted into counterparty meetings that reach E2 or better.',
  E11: 'No regulator-sensitive communication leaves without approval.',
  E12: 'A capital or co-founder conversation that reaches a concrete next gate (E4).',
  E13: 'Day 15, 30 and 45 gates completed with written decisions.',
};

export const SEED_TOP3 = [
  {
    text: 'Build and contact the first 10 Dubai Tier-A sponsor accounts.',
    expectedEvidence: 'E1',
    expectedNote: 'Replies / meetings booked from named Tier-A sponsors',
  },
  {
    text: 'Book architecture conversations with at least two relevant Dubai regulated execution partners.',
    expectedEvidence: 'E1',
    expectedNote: 'Two architecture conversations scheduled',
  },
  {
    text: 'Book the first Saudi B2B discovery call with a Contribution / CMA / FinTech prospect.',
    expectedEvidence: 'E1',
    expectedNote: 'One discovery call scheduled',
  },
];

function slug(s) {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export { slug };
